import type { AsyncDuckDB } from '@duckdb/duckdb-wasm';
import { TableRef, TableRegistrationOptions, TableSourceType } from '../core/engine';
import { Query, TableSchema, TableFormat, ColumnSchema, TableDataSource } from '../core/types';
import { DataTableCache } from '../core/data-table-cache';
import { 
  mapDuckDBTypeToColumnType, 
  cleanFileName, 
  getTableFormatFromExtension 
} from './utils';
import { getDuckDB } from './initialization';

/**
 * DuckDB connection interface for table registration operations
 */
export interface DuckDBConnection {
  query: (sql: string) => Promise<any>;
  insertArrowTable?: (table: any, options: any) => Promise<any>;
  close: () => Promise<void>;
}

/**
 * Register a file with DuckDB and create a view
 * This is a pure function that handles the core file registration logic
 * @param params Configuration object for file registration
 * @returns Promise resolving to TableRef
 */
export async function registerFileInDuckDB(params: {
  file: File;
  db: AsyncDuckDB;
  options?: TableRegistrationOptions & {
    upsert?: boolean; // Whether to replace existing table/view if it exists
  };
}): Promise<TableRef> {
  const { file, db, options } = params;

  // Create a clean name while preserving the original case and format
  const cleanedFileName = cleanFileName({ filename: file.name });

  // Create valid DuckDB identifiers
  const registeredFileName = `file_${cleanedFileName}`; // Internal file registration name
  const viewName = cleanedFileName; // Use the same name for view and TableRef
  
  // Determine the file format
  const format = getTableFormatFromExtension({ filename: file.name });

  // Register the file with DuckDB using the registered file name
  const duckdb = await getDuckDB();
  await db.registerFileHandle(registeredFileName, file, duckdb.DuckDBDataProtocol.BROWSER_FILEREADER, false);
  
  // Create a connection to execute SQL
  const conn = await db.connect();
  
  try {
    // Check if object (table or view) already exists
    const existingObjects = await conn.query(`
      SELECT table_name, table_type
      FROM information_schema.tables 
      WHERE table_schema = 'main' 
      AND table_name = '${viewName}'
    `);

    // If object exists and upsert is not enabled, throw error
    if (existingObjects.toArray().length > 0 && !options?.upsert) {
      throw new Error(`Table/view "${viewName}" already exists. Use upsert option to replace.`);
    }

    // Drop existing object if upsert is enabled
    if (existingObjects.toArray().length > 0 && options?.upsert) {
      // Drop both table and view to ensure clean slate
      await conn.query(`DROP TABLE IF EXISTS "${viewName}"`);
      await conn.query(`DROP VIEW IF EXISTS "${viewName}"`);
    }

    // Create view SQL based on format - use distinct names for file and view
    const viewSql = buildViewSQLForFile({ 
      format, 
      viewName, 
      registeredFileName 
    });
    
    await conn.query(viewSql);
    
    // Query the view to get the schema
    const result = await conn.query(`SELECT * FROM "${viewName}" LIMIT 10`);
    
    // Create schema from result
    const schema: TableSchema = {
      columns: result.schema.fields.map((field: any) => {
        const duckDBType = field.type.toString();
        const mappedType = mapDuckDBTypeToColumnType({ duckDBType });
        return {
          name: field.name,
          type: mappedType,
          nullable: true
        };
      })
    };

    // Create and return TableRef directly
    return {
      id: file.name,
      path: viewName, // Keep using viewName for path since it needs to be SQL-safe
      schema,
      source: {
        type: TableSourceType.LOCAL_FILE,
        fileId: registeredFileName,
        format,
        schema
      }
    };
  } finally {
    await conn.close();
  }
}

/**
 * Build SQL for creating a view from a file based on format
 * @param params Configuration object with format, view name, and file name
 * @returns SQL string for creating the view
 */
export function buildViewSQLForFile(params: {
  format: TableFormat;
  viewName: string;
  registeredFileName: string;
}): string {
  const { format, viewName, registeredFileName } = params;
  
  switch (format) {
    case TableFormat.PARQUET:
      return `CREATE OR REPLACE VIEW "${viewName}" AS SELECT * FROM parquet_scan('${registeredFileName}')`;
    case TableFormat.JSON:
      return `CREATE OR REPLACE VIEW "${viewName}" AS SELECT * FROM read_json('${registeredFileName}', auto_detect=true)`;
    case TableFormat.ARROW:
      return `CREATE OR REPLACE VIEW "${viewName}" AS SELECT * FROM arrow_scan('${registeredFileName}')`;
    case TableFormat.TSV:
      return `CREATE OR REPLACE VIEW "${viewName}" AS SELECT * FROM read_csv('${registeredFileName}', delim='\t', header=true, auto_detect=true)`;
    default:
      return `CREATE OR REPLACE VIEW "${viewName}" AS SELECT * FROM read_csv_auto('${registeredFileName}', sample_size=100, all_varchar=false, header=true, auto_type_candidates=['INTEGER', 'BIGINT', 'DOUBLE', 'TIMESTAMP', 'DATE', 'TIME', 'BOOLEAN', 'VARCHAR'])`;
  }
}

/**
 * Build SQL for creating a view from a URL/file source based on format
 * @param params Configuration object with format, view name, and source URL
 * @returns SQL string for creating the view
 */
export function buildViewSQLForURL(params: {
  format: TableFormat;
  viewName: string;
  sourceUrl: string;
}): string {
  const { format, viewName, sourceUrl } = params;
  
  switch (format) {
    case TableFormat.PARQUET:
      return `CREATE OR REPLACE VIEW "${viewName}" AS SELECT * FROM parquet_scan('${sourceUrl}')`;
    case TableFormat.CSV:
      return `CREATE OR REPLACE VIEW "${viewName}" AS SELECT * FROM read_csv_auto('${sourceUrl}')`;
    case TableFormat.TSV:
      return `CREATE OR REPLACE VIEW "${viewName}" AS SELECT * FROM read_csv('${sourceUrl}', delim='\t', auto_detect=true)`;
    case TableFormat.JSON:
      return `CREATE OR REPLACE VIEW "${viewName}" AS SELECT * FROM read_json_auto('${sourceUrl}')`;
    default:
      throw new Error(`Unsupported format for URL source: ${format}`);
  }
}

/**
 * Get file extension based on table format
 * @param params Configuration object with format
 * @returns File extension string
 */
export function getFileExtension(params: { format: TableFormat }): string {
  const { format } = params;
  
  switch (format) {
    case TableFormat.PARQUET: return '.parquet';
    case TableFormat.CSV: return '.csv';
    case TableFormat.TSV: return '.tsv';
    case TableFormat.JSON: return '.json';
    default: return '';
  }
}

/**
 * Register a table with DuckDB based on the source type
 * @param params Configuration object for table registration
 */
export async function registerTableWithDuckDB(params: {
  conn: DuckDBConnection;
  db: AsyncDuckDB;
  cache: DataTableCache;
  id: string;
  source: TableDataSource;
}): Promise<void> {
  const { conn, db, cache, id, source } = params;
  
  switch (source.type) {
    case TableSourceType.MATERIALIZED:
      if (!conn.insertArrowTable) {
        throw new Error('DuckDB connection does not support insertArrowTable method');
      }
      await conn.insertArrowTable(source.data.table, {
        name: id
      });
      break;

    case TableSourceType.LOCAL_FILE:
      await registerLocalFileSource({ conn, db, id, source });
      break;

    case TableSourceType.URL:
      await registerURLSource({ conn, id, source });
      break;

    case TableSourceType.DATATABLE:
      await registerDatatableSource({ conn, db, cache, id, source });
      break;
  }
}

/**
 * Register a local file source with DuckDB
 * @param params Configuration object for local file registration
 */
async function registerLocalFileSource(params: {
  conn: DuckDBConnection;
  db: AsyncDuckDB;
  id: string;
  source: TableDataSource & { type: TableSourceType.LOCAL_FILE };
}): Promise<void> {
  const { conn, db, id, source } = params;
  
  // Get the file content directly from the source
  if (!source.file) {
    throw new Error(`File not provided for fileId: ${source.fileId}`);
  }
  
  // Read the file data
  const buffer = await source.file.arrayBuffer();
  
  // Register the file data directly with DuckDB
  await db.registerFileBuffer(
    source.fileId,
    new Uint8Array(buffer)
  );
  
  // Create view from the registered file based on format for lazy loading
  const sql = buildViewSQLForFile({
    format: source.format,
    viewName: id,
    registeredFileName: source.fileId
  });
  
  await conn.query(sql);
}

/**
 * Register a URL source with DuckDB
 * @param params Configuration object for URL registration
 */
async function registerURLSource(params: {
  conn: DuckDBConnection;
  id: string;
  source: TableDataSource & { type: TableSourceType.URL };
}): Promise<void> {
  const { conn, id, source } = params;
  
  // Register view directly from URL (no caching for generic URLs)
  const sql = buildViewSQLForURL({
    format: source.format,
    viewName: id,
    sourceUrl: source.url
  });
  
  await conn.query(sql);
}

/**
 * Register a datatable source with DuckDB (with optional caching)
 * @param params Configuration object for datatable registration
 */
async function registerDatatableSource(params: {
  conn: DuckDBConnection;
  db: AsyncDuckDB;
  cache: DataTableCache;
  id: string;
  source: TableDataSource & { type: TableSourceType.DATATABLE };
}): Promise<void> {
  const { conn, db, cache, id, source } = params;
  
  const datatableFormat = source.format;
  const materialized = source.materialized ?? false;
  
  if (materialized) {
    // Download and register view from cache
    try {
      // Use the explicit datatable ID for cache operations
      const cacheId = source.datatableId;
      
      // Check if already cached
      let cachedTable = await cache.get(cacheId);
      
      if (!cachedTable) {
        // Download and cache the file
        cachedTable = await cache.downloadAndCache({
          dataTableId: cacheId,
          datasetId: cacheId, // For datasets, use the same ID for both
          url: source.url,
          format: datatableFormat,
          version: 1
        });
      }
      
      // Create filename with proper extension based on cached format
      const cachedFileName = `cached_${id}${getFileExtension({ format: cachedTable.format })}`;
      
      // Register the cached file with DuckDB using proper extension
      await db.registerFileBuffer(
        cachedFileName,
        cachedTable.fileData
      );
      
      // Create view from cached file
      const sql = buildViewSQLForURL({
        format: cachedTable.format,
        viewName: id,
        sourceUrl: cachedFileName
      });
      
      await conn.query(sql);
    } catch (error) {
      console.error(`Cache failed for ${source.url}, falling back to direct URL:`, error);
      // Fallback to direct URL registration
      const sql = buildViewSQLForURL({
        format: datatableFormat,
        viewName: id,
        sourceUrl: source.url
      });
      
      await conn.query(sql);
    }
  } else {
    // Register view directly from URL
    const sql = buildViewSQLForURL({
      format: datatableFormat,
      viewName: id,
      sourceUrl: source.url
    });
    
    await conn.query(sql);
  }
}

/**
 * Infer schema from a URL data source
 * @param params Configuration object with URL and database connection
 * @returns Promise resolving to inferred table schema
 */
export async function inferSchemaFromURL(params: { 
  url: string; 
  db: AsyncDuckDB; 
}): Promise<TableSchema> {
  const { url, db } = params;
  
  const conn = await db.connect();
  try {
    // Run a simple query directly against the URL to get sample data
    const query = `SELECT * FROM '${url}' LIMIT 10`;
    
    // Execute the query to get an Arrow table
    const result = await conn.query(query);
    
    // Extract schema information from the Arrow table
    const columns: ColumnSchema[] = result.schema.fields.map((field: any) => {
      const dataType = field.type;
      
      return {
        name: field.name,
        type: dataType,
        nullable: field.nullable
      };
    });
    
    if (columns.length === 0) {
      throw new Error('Could not infer schema: no columns detected');
    }
    
    return { columns };
  } finally {
    await conn.close();
  }
}

/**
 * Infer schema from an already-created local table/view
 * @param params Configuration object with connection and table path
 * @returns Promise resolving to inferred table schema
 */
export async function inferSchemaFromLocalTable(params: {
  conn: DuckDBConnection; 
  tablePath: string;
}): Promise<TableSchema> {
  const { conn, tablePath } = params;
  
  // Query the local table to get sample data and schema
  const query = `SELECT * FROM "${tablePath}" LIMIT 10`;
  
  // Execute the query to get an Arrow table
  const result = await conn.query(query);
  
  // Extract schema information from the Arrow table
  const columns: ColumnSchema[] = result.schema.fields.map((field: any) => {
    const dataType = field.type;
    
    return {
      name: field.name,
      type: dataType,
      nullable: field.nullable
    };
  });
  
  if (columns.length === 0) {
    throw new Error('Could not infer schema: no columns detected');
  }
  
  return { columns };
} 